import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, CheckCircle, FileText, Shield, LogOut, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';

export default function PendingApprovalPage() {
  const { t } = useTranslation('doctor-platform');
  const navigate = useNavigate();
  const { user, loading, logout, refresh } = useAuth();

  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = () => {
      if (!loading && !user) {
        // Not authenticated, redirect to login
        navigate('/auth/login', { replace: true });
      }
    };

    // Check approval status every 30 seconds
    const interval = setInterval(() => {
      refresh();
    }, 30000);
    
    checkAuth();

    return () => clearInterval(interval);
  }, [user, loading, navigate, refresh]);

  const handleSignOut = async () => {
    await logout();
    navigate('/auth/login', { replace: true });
  };

  const handleRefresh = () => {
    refresh();
  };

  // Show loading state while authenticating
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">
            {t('auth.pendingApproval.loading')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          {/* Main Card */}
          <Card className="shadow-xl">
            <CardHeader className="text-center pb-8">
              <div className="mx-auto mb-4 p-4 bg-yellow-100 dark:bg-yellow-900 rounded-full w-fit">
                <Clock className="h-12 w-12 text-yellow-600 dark:text-yellow-400" />
              </div>
              <CardTitle className="text-3xl font-bold">
                {user?.role === 'doctor' ? t('auth.pendingApproval.approvedTitle') : t('auth.pendingApproval.pendingTitle')}
              </CardTitle>
              <CardDescription className="text-lg mt-2">
                {user?.role === 'doctor' 
                  ? t('auth.pendingApproval.approvedDescription')
                  : t('auth.pendingApproval.pendingDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Message */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {user?.role === 'doctor' 
                    ? t('auth.pendingApproval.approvedStatus')
                    : t('auth.pendingApproval.pendingStatus')}
                </p>
              </div>

              {/* What happens next */}
              <div>
                <h3 className="font-semibold mb-4">{t('auth.pendingApproval.whatNext')}</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <p className="font-medium">{t('auth.pendingApproval.credentialVerification.title')}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('auth.pendingApproval.credentialVerification.description')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <FileText className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <p className="font-medium">{t('auth.pendingApproval.practiceValidation.title')}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('auth.pendingApproval.practiceValidation.description')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Shield className="h-5 w-5 text-purple-500 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <p className="font-medium">{t('auth.pendingApproval.accountActivation.title')}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('auth.pendingApproval.accountActivation.description')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <p className="text-sm">
                  <span className="font-medium">{t('auth.pendingApproval.support')}</span>{' '}
                  <a href="mailto:support@example.com" className="text-blue-600 hover:underline">
                    support@example.com
                  </a>
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4">
                <Button variant="outline" onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('auth.pendingApproval.signOut')}
                </Button>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => navigate('/dashboard')}
                    variant="outline"
                  >
                    {t('auth.pendingApproval.dashboard')}
                  </Button>
                  <Button 
                    onClick={handleRefresh}
                    variant="default"
                  >
                    {t('auth.pendingApproval.checkStatus')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Info */}
          <div className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
            <p>
              {t('auth.pendingApproval.documentation')}{' '}
              <a href="#" className="text-blue-600 hover:underline">
                {t('auth.pendingApproval.documentationLink')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
